import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  courseDetailQueryOptions,
  coursePaymentsQueryOptions,
} from '@/features/courses/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { gatewaySchema } from '@/schemas/payments'
import { parseId } from '@/screens/courses/lib/params'

/**
 * `/courses/$courseId/checkout?gateway=…`. `gateway` is the pick the page
 * writes back as the member makes it, and what a failed payment's "Try again"
 * link carries so the same gateway is chosen again. A value that is not one
 * of the three gateways is dropped — the member picks again — rather than
 * failing the page.
 */
export const courseCheckoutSearchSchema = z.object({
  gateway: gatewaySchema.optional().catch(undefined),
})
export type CourseCheckoutSearch = z.infer<typeof courseCheckoutSearchSchema>

/**
 * The `/courses/$courseId/checkout` loader.
 *
 * MAIN: the course, the same read its own page and the player share (so it is
 * usually cached already). Everything on the page is drawn from it: the card,
 * the charged price, the access period, and whether the member already has it
 * or it is free. `null` (`COURSE_NOT_FOUND`) is the screen's not-found state,
 * never this route's error page.
 *
 * SECONDARY: the member's course payments, started and not waited for. The
 * page reads them for one thing — a recent Pending attempt for this course,
 * which holds Pay back behind a warning — so a page that opens before they
 * answer simply has no warning yet, and draws it when they do. The gateways
 * are a fixed list, and no read says in advance whether one is set up: the
 * first sign is a 503 at Pay.
 *
 * A `$courseId` that is not a positive integer is answered without a
 * request: the loader returns, and the screen renders not-found before its
 * suspense read.
 */
export async function loadCourseCheckout({
  context: { queryClient },
  params,
}: {
  context: RouterContext
  params: { courseId: string }
}): Promise<void> {
  const courseId = parseId(params.courseId)
  if (courseId === null) return
  prefetchRouteData(queryClient, coursePaymentsQueryOptions())
  await ensureRouteData(queryClient, courseDetailQueryOptions(courseId))
}
